/**
 * Codex cloud onboarding.
 *
 * Runs a DEDICATED browser `codex login` into an ISOLATED CODEX_HOME (so it never
 * touches the user's own ~/.codex — cloud-side token rotation must not log them out
 * locally), reads the resulting auth.json, and POSTs it to the Synkro API's
 * authenticated `/codex/seed` endpoint. The API derives (org, user) from the caller's
 * JWT and forwards it to the internal credential service — one Codex subscription
 * session per (org, user). The browser approval IS the user's consent.
 *
 * See memory: reference_codex_cloud_auth / feedback_codex_credservice_internal_only.
 */
import { spawn as nodeSpawn, spawnSync } from 'node:child_process';
import { readFileSync, mkdirSync, rmSync, existsSync } from 'node:fs';
import { homedir } from 'node:os';
import { join } from 'node:path';

const SYNKRO_DIR = join(homedir(), '.synkro');
// Isolated home for the cloud session — distinct from the user's ~/.codex.
const CODEX_CLOUD_HOME = join(SYNKRO_DIR, 'codex-cloud-session');

export interface CodexCloudResult { ok: boolean; error?: string; }

// The user's own codex install, unless overridden (e.g. the @openai/codex-sdk-bundled
// binary) — the standalone homebrew build is sometimes broken.
function findCodexBinary(): string | null {
  if (process.env.SYNKRO_CODEX_BIN) return process.env.SYNKRO_CODEX_BIN;
  const r = spawnSync('which', ['codex'], { encoding: 'utf-8' });
  const p = (r.stdout || '').trim().split('\n')[0];
  return p || null;
}

function runCodexLogin(codexBin: string): Promise<void> {
  mkdirSync(CODEX_CLOUD_HOME, { recursive: true, mode: 0o700 });
  return new Promise((resolve, reject) => {
    // stdio inherited so the user sees the login prompt + the browser opens; the
    // isolated CODEX_HOME keeps this session separate from ~/.codex.
    const proc = nodeSpawn(codexBin, ['login'], {
      stdio: 'inherit',
      env: { ...process.env, CODEX_HOME: CODEX_CLOUD_HOME },
    });
    proc.on('error', (err) => reject(new Error(`failed to spawn codex login: ${err.message}`)));
    proc.on('close', (code) => (code === 0 ? resolve() : reject(new Error(`codex login exited with code ${code}`))));
  });
}

export async function setupCodexCloud(
  gatewayUrl: string,
  bearerToken: string,
  onStatus?: (m: string) => void,
): Promise<CodexCloudResult> {
  const codexBin = findCodexBinary();
  if (!codexBin) {
    return { ok: false, error: 'Codex CLI not found on PATH. Install Codex (https://developers.openai.com/codex), then re-run `synkro install`. (Set SYNKRO_CODEX_BIN to override the binary path.)' };
  }

  onStatus?.('Opening your browser to authorize your Codex subscription for Synkro Cloud…');
  try {
    await runCodexLogin(codexBin);
  } catch (e) {
    return { ok: false, error: `Codex login failed: ${(e as Error).message}` };
  }

  const authPath = join(CODEX_CLOUD_HOME, 'auth.json');
  if (!existsSync(authPath)) {
    return { ok: false, error: 'codex login completed but no auth.json was written — did the browser approval finish?' };
  }
  let auth: unknown;
  try {
    auth = JSON.parse(readFileSync(authPath, 'utf-8'));
  } catch (e) {
    return { ok: false, error: `could not read codex auth.json: ${(e as Error).message}` };
  }
  // A refresh_token is required or cloud-side refresh can't keep the session alive.
  if (!auth || typeof auth !== 'object' || !((auth as { tokens?: { refresh_token?: unknown } }).tokens?.refresh_token)) {
    return { ok: false, error: 'codex auth.json has no refresh token — cloud refresh would fail. Re-run login.' };
  }

  onStatus?.('Connecting your Codex session to Synkro Cloud…');
  let resp: Response;
  try {
    resp = await fetch(`${gatewayUrl.replace(/\/+$/, '')}/codex/seed`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${bearerToken}` },
      body: JSON.stringify({ auth }),
    });
  } catch (e) {
    return { ok: false, error: `failed to reach Synkro API: ${(e as Error).message}` };
  }
  if (!resp.ok) {
    const detail = await resp.text().catch(() => '');
    return { ok: false, error: `seed rejected (${resp.status}): ${detail.slice(0, 200)}` };
  }

  // Success — the cloud now owns + refreshes this session. Scrub the local copy: it
  // goes stale after the first cloud-side rotation and is a token sitting at rest.
  try { rmSync(CODEX_CLOUD_HOME, { recursive: true, force: true }); } catch {}
  onStatus?.('✓ Codex subscription connected to Synkro Cloud.');
  return { ok: true };
}
